import {
  faCheck,
  faTimes,
  faMicrophone,
  faStop,
  faPlay,
  faPause,
  faRedo,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/quiz.style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import questionsData from "../data/questions.json";
import { trackQuizEvent, trackUserJourney, trackError } from "../utils/ga4";
import logoHeader from "../assets/images/logo-header.png";
import timerIcon from "../assets/images/timer.svg";
import mascotMain from "../assets/images/mastcot-main.png";
import mascotMainShadow from "../assets/images/main-shadow.png";
import backControl from "../assets/images/back-button.png";
import checkIconBlack from "../assets/images/option-checked-black.png";
import editIcon from "../assets/images/edit-icon.png";
import shoes from "../assets/images/shoes.png";
import cat from "../assets/images/cat.png";

// Question Types Interface
interface Question {
  id: number;
  type: "slider" | "options" | "text" | "voice" | "prompt" | "image-choice";
  question: string;
  category: string;
  min?: number;
  max?: number;
  labels?: string[];
  options?: {
    question: string;
    value: number;
  }[];
  question_list?: {
    question: string;
    type: string;
  }[];
  image_url: string;
}

interface QuestionsData {
  questions: Question[];
  metadata: {
    version: string;
    lastUpdated: string;
    totalQuestions: number;
    categories: string[];
  };
}

const QuizPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionType, setQuestionType] = useState<string>("slider");

  // Store answers per question index
  const [answers, setAnswers] = useState<{
    [key: number]: {
      sliderValue: number;
      selectedOptions: string[];
      selectedOptionValues: number[];
      textAnswer: string;
      voiceAnswer: Blob | null;
      promptAnswer: string;
      subQuestionAnswers: string[]; // For sub-questions answers
    };
  }>({});

  // Track if user has interacted with slider
  const [sliderInteracted, setSliderInteracted] = useState<{
    [key: number]: boolean;
  }>({});
  const [rippleEffect, setRippleEffect] = useState<{ [key: number]: number }>(
    {}
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState<number | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [flashLeft, setFlashLeft] = useState(0);
  const [flashWidth, setFlashWidth] = useState(0);
  const [lastValue, setLastValue] = useState<any>(null);
  const [tutorialActive, setTutorialActive] = useState(true);
  const [tutorialValue, setTutorialValue] = useState<number | null>(2);

  // Timer state
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  // Timer functions
  const startTimer = () => {
    if (timerInterval) return; // Already running

    const interval = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    setTimerInterval(interval);
    console.log("⏰ Timer started");
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
      console.log("⏹️ Timer stopped at:", timeElapsed, "seconds");
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Start timer when component mounts
  useEffect(() => {
    startTimer();

    // Cleanup timer on unmount
    return () => {
      stopTimer();
    };
  }, []);


  // Reset temp values when question changes
  useEffect(() => {
    setTempTextAnswer("");
    setTempSubQuestionAnswer("");
  }, [currentQuestion]);

  // Ripple effect function for slider circles
  const triggerRippleEffect = (questionIndex: number, nodeIndex: number) => {
    const rippleKey = questionIndex * 10 + nodeIndex;
    setRippleEffect((prev) => ({
      ...prev,
      [rippleKey]: Date.now(),
    }));

    // Clear ripple after animation
    setTimeout(() => {
      setRippleEffect((prev) => {
        const newState = { ...prev };
        delete newState[rippleKey];
        return newState;
      });
    }, 600);
  };

  // Slider tutorial
  const playTutorial = () => {
    const totalNodes = 5;
    const mid = Math.floor(totalNodes / 2);
    const interval = 300; // interval ระหว่าง node

    // ไล่จากกลาง → ขวา
    for (let i = mid; i < totalNodes; i++) {
      setTimeout(() => {
        setTutorialValue(i);

        const last = i === mid ? i : i - 1;
        const left = (Math.min(last, i) / (totalNodes - 1)) * 100;
        const width = (Math.abs(i - last) / (totalNodes - 1)) * 100;
        setFlashLeft(left);
        setFlashWidth(width);
        setShowFlash(true);

        setTimeout(() => setShowFlash(false), interval - 50);
      }, (i - mid) * interval);
    }

    // ไล่จากขวาสุด → ซ้ายสุด เริ่มหลังกลาง→ขวาเสร็จ
    const startRightToLeft = (totalNodes - mid) * interval; // เวลาที่กลาง→ขวาเสร็จ
    for (let i = totalNodes - 1; i >= 0; i--) {
      setTimeout(() => {
        setTutorialValue(i);

        const last = i + 1 < totalNodes ? i + 1 : totalNodes - 1;
        const left = (Math.min(last, i) / (totalNodes - 1)) * 100;
        const width = (Math.abs(last - i) / (totalNodes - 1)) * 100;
        setFlashLeft(left);
        setFlashWidth(width);
        setShowFlash(true);

        setTimeout(() => setShowFlash(false), interval - 50);
      }, startRightToLeft + (totalNodes - 1 - i) * interval);
    }

    // หลัง tutorial ให้ user ใช้งานได้
    const totalDuration = startRightToLeft + totalNodes * interval;
    setTimeout(() => {
      setTutorialActive(false);
      setTutorialValue(null);
      setShowFlash(false);
    }, totalDuration);
  };

  useEffect(() => {
    playTutorial();
  }, []);

  // Drag functions for slider
  const handleDragStart = (
    e: React.MouseEvent | React.TouchEvent,
    questionIndex: number
  ) => {
    e.preventDefault();
    setIsDragging(true);

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const sliderTrack = e.currentTarget;

    if (sliderTrack) {
      const rect = sliderTrack.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const startValue = Math.round(percentage * 4);

      setDragValue(startValue);
      setLastValue(startValue);
      triggerRippleEffect(questionIndex, startValue);
    }
  };

  const handleDragEnd = (questionIndex: number) => {
    if (!isDragging || dragValue === null) return;

    // Update answer with final position
    updateAnswerForQuestion(questionIndex, { sliderValue: dragValue });
    setSliderInteracted((prev) => ({
      ...prev,
      [questionIndex]: true,
    }));

    // Reset drag state
    setIsDragging(false);
    setDragValue(null);
    setLastValue(null);
  };

  const handleDragMove = (e: any) => {
    if (!isDragging) return;

    const trackRect = e.currentTarget.getBoundingClientRect
      ? e.currentTarget.getBoundingClientRect()
      : document.querySelector(".slider-track")?.getBoundingClientRect();

    if (!trackRect) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const relativeX = Math.max(
      0,
      Math.min(clientX - trackRect.left, trackRect.width)
    );
    const value = Math.round((relativeX / trackRect.width) * 4);

    setDragValue(value);

    // มีการเปลี่ยนค่า -> trigger flash
    if (lastValue !== null && lastValue !== value) {
      const left = (Math.min(lastValue, value) / 4) * 100;
      const width = (Math.abs(value - lastValue) / 4) * 100;

      setFlashLeft(left);
      setFlashWidth(width);
      setShowFlash(true);

      // reset flash
      clearTimeout((handleDragMove as any)._flashTimeout);
      (handleDragMove as any)._flashTimeout = setTimeout(() => {
        setShowFlash(false);
      }, 300);
    }

    setLastValue(value);
  };

  // Global mouse/touch event handlers for smooth dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const sliderTrack = document.querySelector(".slider-track");
        if (sliderTrack) {
          const rect = sliderTrack.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, x / rect.width));
          const newValue = Math.round(percentage * 4);
          setDragValue(newValue);
        }
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleDragEnd(currentQuestion);
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
        const sliderTrack = document.querySelector(".slider-track");
        if (sliderTrack && e.touches[0]) {
          const rect = sliderTrack.getBoundingClientRect();
          const x = e.touches[0].clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, x / rect.width));
          const newValue = Math.round(percentage * 4);
          setDragValue(newValue);
        }
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        handleDragEnd(currentQuestion);
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDragging, currentQuestion, dragValue]);

  // UI state (not tied to questions)
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showPromptPopup, setShowPromptPopup] = useState(false);
  const [tempPromptAnswer, setTempPromptAnswer] = useState("");
  const [editingAnswerIndex, setEditingAnswerIndex] = useState<number | null>(
    null
  );
  const [tempTextAnswer, setTempTextAnswer] = useState<string>("");
  const [tempSubQuestionAnswer, setTempSubQuestionAnswer] =
    useState<string>("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  const dynamicTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize textarea when content changes
  useEffect(() => {
    if (dynamicTextareaRef.current) {
      const textarea = dynamicTextareaRef.current;
      textarea.style.height = 'auto';
      const lineHeight = 24; // 1.5 * 16px font-size
      const maxHeight = lineHeight * 5; // 5 rows maximum
      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
    }
  }, [tempSubQuestionAnswer]);

  // Helper function to handle edit answer
  const handleEditAnswer = (answerIndex: number) => {
    setEditingAnswerIndex(answerIndex);
    // Prefill temp value with current answer
    const currentAnswer =
      getAnswerForQuestion(currentQuestion).subQuestionAnswers[answerIndex] ||
      "";
    setTempSubQuestionAnswer(currentAnswer);
  };

  // Helper function to check if all questions are answered
  const allQuestionsAnswered = () => {
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const answer = getAnswerForQuestion(i);

      if (question.category === "multiple_question") {
        // Check if all sub-questions are answered
        const answeredCount = answer.subQuestionAnswers.filter(
          (a) => a && a.trim()
        ).length;
        const totalCount = question.question_list?.length || 0;
        if (answeredCount < totalCount) {
          return false;
        }
      } else if (question.type === "text") {
        // Check if text question is answered
        if (!answer.textAnswer || !answer.textAnswer.trim()) {
          return false;
        }
      } else if (question.type === "slider") {
        // Slider always has a value (default 5), so always considered answered
        continue;
      } else if (question.type === "options") {
        // Check if options are selected
        if (!answer.selectedOptions || answer.selectedOptions.length === 0) {
          return false;
        }
      }
    }
    return true;
  };

  // Helper function to check if it's the last question
  const isLastQuestion = () => {
    return currentQuestion === questions.length - 1;
  };

  // Helper function to submit quiz
  const submitQuiz = async () => {
    try {
      console.log("🚀 Preparing quiz data for thank-you page:", answers);

      // Stop the timer
      stopTimer();

      // Format answers with question type information
      const formattedAnswers: { [key: string]: any } = {};

      Object.keys(answers).forEach((answerKey) => {
        const questionIndex = parseInt(answerKey);
        const question = questionsData.questions[questionIndex];
        const answer = answers[questionIndex];

        if (question) {
          formattedAnswers[answerKey] = {
            questionType: question.type,
            sliderValue: question.type === "slider" ? answer.sliderValue : 0,
            selectedOptions:
              question.type === "options" ? answer.selectedOptions : [],
            selectedOptionValues:
              question.type === "options" ? answer.selectedOptionValues : [],
            textAnswer: question.type === "text" ? answer.textAnswer : "",
            voiceAnswer: answer.voiceAnswer,
            promptAnswer: answer.promptAnswer,
            subQuestionAnswers:
              question.type === "text" ? answer.subQuestionAnswers : [],
          };
        }
      });

      // Store quiz answers in sessionStorage for the thank-you page
      const quizData = {
        answers: formattedAnswers,
        sessionId: `quiz_session_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        submittedAt: new Date().toISOString(),
        timeElapsed: timeElapsed, // Add timer data
        timeFormatted: formatTime(timeElapsed), // Add formatted time
      };

      sessionStorage.setItem("quizData", JSON.stringify(quizData));

      // Track quiz completion
      trackQuizEvent('quiz_completed', questionsData.questions.length);
      trackUserJourney('quiz_submitted', {
        time_elapsed: timeElapsed,
        questions_answered: Object.keys(formattedAnswers).length,
        session_id: quizData.sessionId
      });

      console.log("✅ Quiz data stored, navigating to thank-you page");
      console.log("⏱️ Total time taken:", formatTime(timeElapsed));
      console.log("📊 Formatted answers:", formattedAnswers);

      // Navigate to thank-you page
      navigate("/thank-you");
    } catch (error) {
      console.error("❌ Failed to prepare quiz data:", error);
      
      // Track error
      trackError('quiz_submission_failed', error instanceof Error ? error.message : 'Unknown error', {
        time_elapsed: timeElapsed,
        questions_answered: Object.keys(answers).length
      });
      
      alert("Failed to prepare quiz data. Please try again.");
    }
  };

  // Helper function to check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const question = getCurrentQuestion();
    const answer = getAnswerForQuestion(currentQuestion);

    if (question.category === "multiple_question") {
      // Check if all sub-questions are answered
      const answeredCount = answer.subQuestionAnswers.filter(
        (a) => a && a.trim()
      ).length;
      const totalCount = question.question_list?.length || 0;
      return answeredCount >= totalCount;
    } else if (question.type === "text") {
      // Check if text question is answered
      return answer.textAnswer && answer.textAnswer.trim();
    } else if (question.type === "slider") {
      // Check if user has interacted with the slider
      return sliderInteracted[currentQuestion] === true;
    } else if (question.type === "options") {
      // Check if options are selected
      return answer.selectedOptions && answer.selectedOptions.length > 0;
    }

    return false;
  };

  // Helper functions for managing answers per question
  const getAnswerForQuestion = (questionIndex: number) => {
    return (
      answers[questionIndex] || {
        sliderValue: 5,
        selectedOptions: [],
        selectedOptionValues: [],
        textAnswer: "",
        voiceAnswer: null,
        promptAnswer: "",
        subQuestionAnswers: [],
      }
    );
  };

  const updateAnswerForQuestion = (
    questionIndex: number,
    updates: Partial<(typeof answers)[number]>
  ) => {
    console.log("🚀 Updating answer for question:", questionIndex, updates);
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        ...getAnswerForQuestion(questionIndex),
        ...updates,
      },
    }));
  };

  // Helper functions for multiple questions
  const getCurrentQuestion = () => {
    return questions[currentQuestion];
  };

  const getCurrentDisplayQuestionIndex = () => {
    const question = getCurrentQuestion();
    if (question.category === "multiple_question" && question.question_list) {
      const answers = getAnswerForQuestion(currentQuestion).subQuestionAnswers;

      // Find the first question that doesn't have an answer yet
      for (let i = 0; i < question.question_list.length; i++) {
        if (i === 0 || answers[i - 1]) {
          // Question is available (first question or previous has answer)
          if (!answers[i]) {
            // Current question hasn't been answered yet
            return i;
          }
        } else {
          // Previous question doesn't have answer, stop here
          return i - 1;
        }
      }

      // All questions have answers, show last question
      return question.question_list.length - 1;
    }
    return 0;
  };

  // Auto-scroll to bottom when new content appears (only for multiple_question)
  // useEffect(() => {
  //   const question = getCurrentQuestion();
  //   if (question.category === 'multiple_question' && carouselContainerRef.current) {
  //     const timeout = setTimeout(() => {
  //       carouselContainerRef.current?.scrollTo({
  //         top: carouselContainerRef.current.scrollHeight,
  //         behavior: 'smooth'
  //       });
  //     }, 100);
  //     return () => clearTimeout(timeout);
  //   }
  // }, [getAnswerForQuestion(currentQuestion).subQuestionAnswers, currentQuestion]);

  const handleOptionSelect = (optionText: string, optionValue: number) => {
    const currentAnswer = getAnswerForQuestion(currentQuestion);
    const isSelected = currentAnswer.selectedOptions.includes(optionText);

    let newSelectedOptions: string[];
    let newSelectedOptionValues: number[];

    if (isSelected) {
      // Remove from selection
      newSelectedOptions = currentAnswer.selectedOptions.filter(
        (item) => item !== optionText
      );
      newSelectedOptionValues = currentAnswer.selectedOptionValues.filter(
        (value) => value !== optionValue
      );
      
      // Track option deselection
      trackQuizEvent('option_deselected', currentQuestion, currentAnswer.questionId);
    } else {
      // Check if maximum 5 choices are already selected
      if (currentAnswer.selectedOptions.length >= 5) {
        // Track max selections reached
        trackQuizEvent('max_selections_reached', currentQuestion, currentAnswer.questionId);
        return; // Don't allow more than 5 selections
      }

      // Add to selection
      newSelectedOptions = [...currentAnswer.selectedOptions, optionText];
      newSelectedOptionValues = [
        ...(currentAnswer.selectedOptionValues || []),
        optionValue,
      ];
      
      // Track option selection
      trackQuizEvent('option_selected', currentQuestion, currentAnswer.questionId);
    }

    updateAnswerForQuestion(currentQuestion, {
      selectedOptions: newSelectedOptions,
      selectedOptionValues: newSelectedOptionValues,
    });
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        updateAnswerForQuestion(currentQuestion, { voiceAnswer: blob });
        setShowVoiceRecorder(false);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Unable to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const startVoiceRecording = () => {
    setShowVoiceRecorder(true);
    setRecordingTime(0);
  };

  const reRecord = () => {
    updateAnswerForQuestion(currentQuestion, { voiceAnswer: null });
    setShowVoiceRecorder(true);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const playAudio = () => {
    const currentVoiceAnswer =
      getAnswerForQuestion(currentQuestion).voiceAnswer;
    if (currentVoiceAnswer && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Prompt Functions

  const handleSubmitPrompt = () => {
    updateAnswerForQuestion(currentQuestion, {
      promptAnswer: tempPromptAnswer,
    });
    setShowPromptPopup(false);
  };

  const handleCancelPrompt = () => {
    setTempPromptAnswer("");
    setShowPromptPopup(false);
  };

  const handleTempPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setTempPromptAnswer(e.target.value);
  };

  // No longer needed - using carousel animation instead of scroll

  // Get questions from JSON file with type safety
  const questions: Question[] = (questionsData as QuestionsData).questions;

  // No longer needed - using carousel instead of scroll with Intersection Observer

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      const nextIndex = currentQuestion + 1;
      console.log(
        `Carousel Next: ${currentQuestion} → ${nextIndex} (translateY: -${
          nextIndex * 100
        }%)`
      );
      setCurrentQuestion(nextIndex);
      setQuestionType(questions[nextIndex].type);
      setEditingAnswerIndex(null); // Reset edit mode when changing questions
      // No need to reset sliderInteracted since we want to preserve per-question state

      // Auto scroll to top when changing questions - delay 2 seconds after DOM update
      setTimeout(() => {
        console.log(
          "Attempting to scroll carousel container to top after 2s delay, element:",
          carouselContainerRef.current
        );
        if (carouselContainerRef.current) {
          carouselContainerRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          console.log("Carousel container scroll executed - Next (2s delay)");
        } else {
          console.log("carouselContainerRef.current is null - Next (2s delay)");
        }
      }, 500);

      // Reset state when moving to next question
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      const prevIndex = currentQuestion - 1;
      console.log(
        `Carousel Back: ${currentQuestion} → ${prevIndex} (translateY: -${
          prevIndex * 100
        }%)`
      );
      setCurrentQuestion(prevIndex);
      setQuestionType(questions[prevIndex].type);
      setEditingAnswerIndex(null); // Reset edit mode when changing questions
      // No need to reset sliderInteracted since we want to preserve per-question state

      // Auto scroll to top when changing questions - delay 2 seconds after DOM update
      setTimeout(() => {
        console.log(
          "Attempting to scroll carousel container to top after 2s delay, element:",
          carouselContainerRef.current
        );
        if (carouselContainerRef.current) {
          carouselContainerRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          console.log("Carousel container scroll executed - Back (2s delay)");
        } else {
          console.log("carouselContainerRef.current is null - Back (2s delay)");
        }
      }, 1000);

      // Reset state when moving to previous question
    }
  };

  return (
    <div className="container">
      <div className="container-fluid-quiz">
        <div className="quiz-page">
          <div className="quiz-header">
            <div className="quiz-header-left">
              <img src={logoHeader} alt="logo" className="icon" />
            </div>
            <div className="quiz-header-center">
              <h1 className="font-bold">
                {String(currentQuestion + 1).padStart(2, "0")} of{" "}
                {String(questions.length).padStart(2, "0")}
              </h1>
            </div>
            <div className="quiz-header-right">
              <div className="quiz-header-right-item">
                <img src={timerIcon} alt="timer" className="icon" />
                <h1>{formatTime(timeElapsed)}</h1>
              </div>
            </div>
          </div>

          <div
            className="questions-carousel-container"
            ref={carouselContainerRef}
          >
            {questions.map(
              (question, index) =>
                index === currentQuestion && (
                  <div
                    key={question.id}
                    className="carousel-question-page"
                    data-question-index={index}
                  >
                    <div className="chat-container">
                      {/* Bot Message */}
                      {/* {
                    index == 0 && (
                      <div className='chat-message bot-message'>
                        <div className={`message-bubble bot-bubble start-text`}>
                          <p className='start-text'>Hello, how can I help you today?</p>
                        </div>
                      </div>
                    )
                  } */}
                      {question.question != null &&
                        question.question !== "" && (
                          <div className="chat-message bot-message">
                            <div
                              className={`message-bubble bot-bubble ${
                                question.type === "image-choice"
                                  ? "image-choice-bubble"
                                  : ""
                              } ${
                                question.question.length > 50 ? "long-text" : ""
                              }`}
                            >
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: question.question,
                                }}
                              ></p>
                            </div>
                          </div>
                        )}
                      {question.image_url && (
                        <div className="prompt-images">
                          <img
                            src={question.image_url == "shoes" ? shoes : question.image_url == "cat" ? cat : ""}
                            alt="example"
                            className="prompt-image"
                          />
                        </div>
                      )}
                      {question.category === "multiple_question" && (
                        <div className="multiple-conversation-flow">
                          {question.question_list?.map((q, index) => {
                            // Show question only if:
                            // 1. It's the first question (index 0)
                            // 2. Previous question has an answer
                            const shouldShowQuestion =
                              index === 0 ||
                              getAnswerForQuestion(currentQuestion)
                                .subQuestionAnswers[index - 1];

                            if (!shouldShowQuestion) return null;

                            return (
                              <div key={index} className="conversation-group">
                                {/* Show question */}
                                <div
                                  className="chat-message bot-message"
                                  data-sub-question={index}
                                >
                                  <div
                                    className={`message-bubble bot-bubble transition-effect ${
                                      q.question.length > 50 ? "long-text" : ""
                                    } ${
                                      index ===
                                        getCurrentDisplayQuestionIndex() &&
                                      !getAnswerForQuestion(currentQuestion)
                                        .subQuestionAnswers[index]
                                        ? "current-question-highlight"
                                        : ""
                                    }`}
                                  >
                                    <p>{q.question}</p>
                                    <div className="sub-question-progress">
                                      {question.question_list?.length &&
                                        question.question_list?.length >= 2 && (
                                          <span>
                                            Question {index + 1} of{" "}
                                            {question.question_list?.length}
                                          </span>
                                        )}
                                      {index ===
                                        getCurrentDisplayQuestionIndex() &&
                                        !getAnswerForQuestion(currentQuestion)
                                          .subQuestionAnswers[index] && (
                                          <span className="waiting-answer">
                                            ⏳ Waiting for your answer...
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                </div>

                                {/* Show answer if exists */}
                                {getAnswerForQuestion(currentQuestion)
                                  .subQuestionAnswers[index] && (
                                  <div className="chat-message user-message">
                                    <div className="message-bubble user-bubble">
                                      <div className="response-container">
                                        <div className="sub-question-response">
                                          <div className="previous-answer-container">
                                            <div className="previous-answer">
                                              <span className="answer-text" >
                                                {
                                                  getAnswerForQuestion(
                                                    currentQuestion
                                                  ).subQuestionAnswers[index]
                                                }
                                              </span>
                                            </div>
                                            <div
                                              className="previous-answer-edit-control"
                                              onClick={() =>
                                                handleEditAnswer(index)
                                              }
                                            >
                                              <img
                                                src={editIcon}
                                                alt="edit"
                                                className="previous-answer-icon"
                                              />
                                              <span className="previous-answer-edit-text">
                                                Edit asnwer
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Show completion message only for the last question with answer */}
                                {index === getCurrentDisplayQuestionIndex() &&
                                  getAnswerForQuestion(currentQuestion)
                                    .subQuestionAnswers[index] &&
                                  index ===
                                    (question.question_list?.length || 0) -
                                      1 && (
                                    <div className="chat-message bot-message">
                                      <div
                                        className={`message-bubble bot-bubble start-text`}
                                      >
                                        <p className="start-text" dangerouslySetInnerHTML={{ __html: currentQuestion == 10
                                            ? "Happy with your answers? Click “Next”! <br> Need to edit something? Just hit “Edit Answer” before you go."
                                            : currentQuestion == 11
                                            ? "Looking good! Edit or hit Next to keep rolling."
                                            : currentQuestion == 12
                                            ? "Good one! Click Next when you’re ready."
                                            : currentQuestion == 13
                                            ? "Thanks! Great progress — let’s see what’s next."
                                            : currentQuestion == 14
                                            ? "You did it! 🎉 Click “Finish” to see how ready you are for Generative AI."
                                            : "Happy with your answers? Click “Next”! <br> Once you’re happy, click the Next button below to move on to the next question!" }}>

                                        </p>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            );
                          })}

                          {/* Current question if not answered yet
                        {currentSubQuestionIndex < (question.question_list?.length || 0) &&
                          !getAnswerForQuestion(currentQuestion).subQuestionAnswers[currentSubQuestionIndex] && (
                            <div className='chat-message bot-message current-question-highlight'>
                              <div className={`message-bubble bot-bubble transition-effect ${getCurrentSubQuestion()?.question.length && getCurrentSubQuestion()!.question.length > 50 ? 'long-text' : ''}`}>
                                <p>{getCurrentSubQuestion()?.question}</p>
                                <div className="sub-question-progress">
                                  {question.question_list?.length >= 2 && (
                                    <span>Question {currentSubQuestionIndex + 1} of {question.question_list?.length}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )} */}
                        </div>
                      )}

                      {/* User Response */}
                      <div className="chat-message user-message">
                        <div className="message-bubble user-bubble">
                          <div className="response-container">
                            {question.category === "multiple_question" ? (
                              <div className="multiple-question-placeholder">
                                {/* Multiple question responses are shown inline */}
                              </div>
                            ) : question.type === "slider" ? (
                              <div className={`slider-container ${tutorialActive ? 'tutorial-active' : ''}`}>
                                <div
                                  className="slider-track"
                                  onMouseDown={(e) =>
                                    !tutorialActive && handleDragStart(e, currentQuestion)
                                  }
                                  onTouchStart={(e) =>
                                    !tutorialActive && handleDragStart(e, currentQuestion)
                                  }
                                  onMouseMove={handleDragMove}
                                  onTouchMove={handleDragMove}
                                >
                                  <div
                                    className="slider-progress"
                                    style={{
                                      width: `${
                                        ((isDragging && dragValue !== null
                                          ? dragValue
                                          : getAnswerForQuestion(
                                              currentQuestion
                                            ).sliderValue) /
                                          4) *
                                        100
                                      }%`,
                                      transition: isDragging
                                        ? "none"
                                        : "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    }}
                                  ></div>

                                  {showFlash && (
                                    <div
                                      className="slider-flash"
                                      style={{
                                        width: `${flashWidth}%`,
                                        left: `${flashLeft}%`,
                                      }}
                                    />
                                  )}

                                  {Array.from({ length: 5 }, (_, index) => {
                                    const value = index;
                                    const currentSliderValue = tutorialActive
                                      ? tutorialValue ?? 0
                                      : isDragging && dragValue !== null
                                      ? dragValue
                                      : getAnswerForQuestion(currentQuestion)
                                          .sliderValue;
                                    const isActive =
                                      value <= currentSliderValue;
                                    const isCurrent =
                                      value === currentSliderValue;
                                    return (
                                      <div
                                        key={value}
                                        className={`slider-node ${
                                          isActive ? "active" : ""
                                        } ${isCurrent ? "current" : ""}`}
                                        style={{
                                          left: `${(index / 4) * 100}%`,
                                          width:
                                            index === 0 || index === 4
                                              ? "40px"
                                              : index === 1 || index === 3
                                              ? "30px"
                                              : "20px",
                                          height:
                                            index === 0 || index === 4
                                              ? "40px"
                                              : index === 1 || index === 3
                                              ? "30px"
                                              : "20px",
                                        }}
                                        onClick={() => {
                                          if (!isDragging && !tutorialActive) {
                                            triggerRippleEffect(
                                              currentQuestion,
                                              index
                                            );
                                            updateAnswerForQuestion(
                                              currentQuestion,
                                              { sliderValue: value }
                                            );
                                            setSliderInteracted((prev) => ({
                                              ...prev,
                                              [currentQuestion]: true,
                                            }));
                                          }
                                        }}
                                      >
                                        <div
                                          className="node-circle"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                          }}
                                        >
                                          {rippleEffect[
                                            currentQuestion * 10 + index
                                          ] && (
                                            <div
                                              className="ripple-effect"
                                              key={
                                                rippleEffect[
                                                  currentQuestion * 10 + index
                                                ]
                                              }
                                            />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="slider-labels">
                                  <span
                                    className="slider-label-left"
                                    dangerouslySetInnerHTML={{
                                      __html: question.labels?.[0] || "Low",
                                    }}
                                  ></span>
                                  <span
                                    className="slider-label-right"
                                    dangerouslySetInnerHTML={{
                                      __html: question.labels?.[1] || "High",
                                    }}
                                  ></span>
                                </div>

                                <div className="mascot-main-control">
                                  <div className="mascot-main-item-control">
                                    <img
                                      src={mascotMain}
                                      alt="mascot"
                                      className="mascot-main-item-img"
                                    />
                                    <img
                                      src={mascotMainShadow}
                                      alt="mascot"
                                      className="mascot-main-item-img-shadow"
                                    />
                                  </div>
                                  <div className="mascot-main-quote-control">
                                    <span className="mascot-main-quote-control-text">
                                      {index == 0
                                        ? "Choose the direction that fits you!"
                                        : index == 1
                                        ? "Which is your normal reaction?"
                                        : index == 2
                                        ? "Pick what feels right."
                                        : index == 3
                                        ? "Choose what feels natural."
                                        : index == 4
                                        ? "Pick your first instinct."
                                        : index == 5
                                        ? "Which side feels more you?"
                                        : "Slide to your!"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : question.type === "options" ? (
                              <div className="option-buttons">
                                {/* <div className='selection-counter'>
                              <span className='selection-counter-text'>
                                Selected: {getAnswerForQuestion(currentQuestion).selectedOptions.length}/5
                              </span>
                            </div> */}
                                {question.options?.map(
                                  (option, optionIndex) => (
                                    <button
                                      key={optionIndex}
                                      className={`option-btn ${
                                        getAnswerForQuestion(
                                          currentQuestion
                                        ).selectedOptions.includes(
                                          option.question
                                        )
                                          ? "selected"
                                          : ""
                                      }`}
                                      data-selection-order={
                                        getAnswerForQuestion(
                                          currentQuestion
                                        ).selectedOptions.includes(
                                          option.question
                                        )
                                          ? optionIndex
                                          : -1
                                      }
                                      onClick={() =>
                                        handleOptionSelect(
                                          option.question,
                                          option.value
                                        )
                                      }
                                    >
                                      <div className="option-icon">
                                        {getAnswerForQuestion(
                                          currentQuestion
                                        ).selectedOptions.includes(
                                          option.question
                                        ) ? (
                                          <img
                                            src={checkIconBlack}
                                            alt="check"
                                            className="check-icon"
                                          />
                                        ) : (
                                          <div className="empty-circle"></div>
                                        )}
                                      </div>
                                      <span className="option-text">
                                        {option.question}
                                      </span>
                                    </button>
                                  )
                                )}
                              </div>
                            ) : question.type === "voice" ? (
                              <div className="voice-input-container">
                                {getAnswerForQuestion(currentQuestion)
                                  .voiceAnswer ? (
                                  <div className="voice-answer-display">
                                    <div className="voice-answer-content">
                                      <div className="audio-controls">
                                        <button
                                          className="play-btn"
                                          onClick={playAudio}
                                        >
                                          <FontAwesomeIcon
                                            icon={isPlaying ? faPause : faPlay}
                                          />
                                        </button>
                                        <div className="audio-info">
                                          <span className="audio-duration">
                                            Voice Recording
                                          </span>
                                          <span className="audio-status">
                                            {isPlaying
                                              ? "Playing..."
                                              : "Ready to play"}
                                          </span>
                                        </div>
                                      </div>
                                      <audio
                                        ref={audioRef}
                                        src={
                                          getAnswerForQuestion(currentQuestion)
                                            .voiceAnswer
                                            ? URL.createObjectURL(
                                                getAnswerForQuestion(
                                                  currentQuestion
                                                ).voiceAnswer!
                                              )
                                            : ""
                                        }
                                        onEnded={() => setIsPlaying(false)}
                                        onPause={() => setIsPlaying(false)}
                                      />
                                    </div>
                                    <button
                                      className="re-record-btn"
                                      onClick={reRecord}
                                    >
                                      <FontAwesomeIcon icon={faRedo} />
                                      Re-record
                                    </button>
                                  </div>
                                ) : showVoiceRecorder ? (
                                  <div className="voice-recorder-container">
                                    <div className="recorder-status">
                                      <div
                                        className={`recording-indicator ${
                                          isRecording ? "recording" : "ready"
                                        }`}
                                      >
                                        <FontAwesomeIcon icon={faMicrophone} />
                                      </div>
                                      <div className="recording-info">
                                        <span className="recording-time">
                                          {formatTime(recordingTime)}
                                        </span>
                                        <span className="recording-status">
                                          {isRecording
                                            ? "Recording..."
                                            : "Ready to record"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="recorder-controls">
                                      {!isRecording ? (
                                        <button
                                          className="start-recording-btn"
                                          onClick={startRecording}
                                        >
                                          <FontAwesomeIcon
                                            icon={faMicrophone}
                                          />
                                          Start Recording
                                        </button>
                                      ) : (
                                        <button
                                          className="stop-recording-btn"
                                          onClick={stopRecording}
                                        >
                                          <FontAwesomeIcon icon={faStop} />
                                          Stop Recording
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    className="start-voice-btn"
                                    onClick={startVoiceRecording}
                                  >
                                    <FontAwesomeIcon icon={faMicrophone} />
                                    Start Answer
                                  </button>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
          {/* Navigation */}
          <div className="navigation-container">
            <img
              src={backControl}
              onClick={prevQuestion}
              alt="back"
              className="back-control"
            />
            {questions[currentQuestion]?.category === "multiple_question" ? (
              // Check if editing mode or all questions answered
              editingAnswerIndex !== null ||
              getAnswerForQuestion(currentQuestion).subQuestionAnswers.filter(
                (answer) => answer
              ).length <
                (questions[currentQuestion]?.question_list?.length || 0) ? (
                <div className="text-input-container">
                  {editingAnswerIndex !== null && (
                    <div className="edit-mode-indicator"></div>
                  )}
                  <textarea
                    ref={dynamicTextareaRef}
                    placeholder={
                      editingAnswerIndex !== null
                        ? "Edit your answer..."
                        : "Type your answer..."
                    }
                    className="text-input-field dynamic-textarea"
                    value={(() => {
                      if (
                        getCurrentQuestion().category === "multiple_question"
                      ) {
                        if (editingAnswerIndex !== null) {
                          return tempSubQuestionAnswer;
                        } else {
                          const displayIndex = getCurrentDisplayQuestionIndex();
                          const savedAnswer =
                            getAnswerForQuestion(currentQuestion)
                              .subQuestionAnswers[displayIndex] || "";
                          return tempSubQuestionAnswer || savedAnswer;
                        }
                      }
                      return "";
                    })()}
                    onChange={(e) => {
                      if (
                        getCurrentQuestion().category === "multiple_question"
                      ) {
                        setTempSubQuestionAnswer(e.target.value);
                      }
                    }}
                    onInput={(e) => {
                      // Auto-resize textarea based on content
                      const textarea = e.target as HTMLTextAreaElement;
                      textarea.style.height = 'auto';
                      const lineHeight = 24; // 1.5 * 16px font-size
                      const maxHeight = lineHeight * 5; // 5 rows maximum
                      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
                    }}
                    rows={1}
                    maxLength={400}
                  />
                 
                  <button
                    className="send-button"
                    onClick={() => {
                      const answer = tempSubQuestionAnswer.trim();

                      if (!answer.trim()) {
                        alert("Please provide an answer before proceeding.");
                        return;
                      }

                      if (editingAnswerIndex !== null) {
                        // Update existing answer
                        const newAnswers = [
                          ...getAnswerForQuestion(currentQuestion)
                            .subQuestionAnswers,
                        ];
                        newAnswers[editingAnswerIndex] = answer;
                        updateAnswerForQuestion(currentQuestion, {
                          subQuestionAnswers: newAnswers,
                        });
                        setEditingAnswerIndex(null); // Exit edit mode
                        setTempSubQuestionAnswer(""); // Clear temp

                        // Scroll to the edited answer
                        setTimeout(() => {
                          if (carouselContainerRef.current) {
                            carouselContainerRef.current.scrollTo({
                              top: carouselContainerRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }
                        }, 100);
                      } else {
                        // Save new answer
                        const displayIndex = getCurrentDisplayQuestionIndex();
                        const newAnswers = [
                          ...getAnswerForQuestion(currentQuestion)
                            .subQuestionAnswers,
                        ];
                        newAnswers[displayIndex] = answer;
                        updateAnswerForQuestion(currentQuestion, {
                          subQuestionAnswers: newAnswers,
                        });
                        setTempSubQuestionAnswer(""); // Clear temp

                        // Auto scroll to next question after updating answer
                        setTimeout(() => {
                          if (carouselContainerRef.current) {
                            carouselContainerRef.current.scrollTo({
                              top: carouselContainerRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }
                        }, 100);

                        // Move to next question (handled by getCurrentDisplayQuestionIndex)
                        if (
                          displayIndex + 1 >=
                          (questions[currentQuestion]?.question_list?.length ||
                            0)
                        ) {
                          // nextQuestion();
                        }
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>
              ) : (
                <div
                  className="text-input-container"
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    boxShadow: "none",
                  }}
                >
                  <button
                    className={
                      isLastQuestion() && allQuestionsAnswered()
                        ? "next-button"
                        : "next-button"
                    }
                    disabled={!isCurrentQuestionAnswered() && !isLastQuestion()}
                    style={{
                      opacity:
                        !isCurrentQuestionAnswered() && !isLastQuestion()
                          ? 0.5
                          : 1,
                      cursor:
                        !isCurrentQuestionAnswered() && !isLastQuestion()
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={
                      isLastQuestion() && allQuestionsAnswered()
                        ? submitQuiz
                        : nextQuestion
                    }
                  >
                    {isLastQuestion() && allQuestionsAnswered()
                      ? "Submit"
                      : "Next"}
                  </button>
                </div>
              )
            ) : questionType === "slider" || questionType === "options" ? (
              <div
                className="text-input-container"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                <button
                  className={
                    isLastQuestion() && allQuestionsAnswered()
                      ? "next-button"
                      : "next-button"
                  }
                  disabled={!isCurrentQuestionAnswered() && !isLastQuestion()}
                  style={{
                    opacity:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? 0.5
                        : 1,
                    cursor:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={
                    isLastQuestion() && allQuestionsAnswered()
                      ? submitQuiz
                      : nextQuestion
                  }
                >
                  {isLastQuestion() && allQuestionsAnswered()
                    ? "Submit"
                    : "Next"}
                </button>
              </div>
            ) : questionType === "text" ? (
              <div className="text-input-container">
                <input
                  type="text"
                  placeholder="Type your answer..."
                  className="text-input-field"
                  value={
                    tempTextAnswer ||
                    getAnswerForQuestion(currentQuestion).textAnswer ||
                    ""
                  }
                  onChange={(e) => {
                    setTempTextAnswer(e.target.value);
                  }}
                />
                <button
                  className={
                    isLastQuestion() && allQuestionsAnswered()
                      ? "submit-button"
                      : "send-button"
                  }
                  disabled={!isCurrentQuestionAnswered() && !isLastQuestion()}
                  style={{
                    opacity:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? 0.5
                        : 1,
                    cursor:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={() => {
                    const textAnswer = tempTextAnswer.trim();

                    if (textAnswer) {
                      updateAnswerForQuestion(currentQuestion, {
                        textAnswer: textAnswer,
                      });
                      setTempTextAnswer(""); // Clear temp

                      if (isLastQuestion() && allQuestionsAnswered()) {
                        submitQuiz();
                      } else {
                        nextQuestion();
                      }

                      // Auto scroll to bottom after updating answer
                      setTimeout(() => {
                        carouselContainerRef.current?.scrollTo({
                          top: carouselContainerRef.current.scrollHeight,
                          behavior: "smooth",
                        });
                      }, 100);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            ) : (
              <div
                className="text-input-container"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                <button
                  className={
                    isLastQuestion() && allQuestionsAnswered()
                      ? "next-button"
                      : "next-button"
                  }
                  disabled={!isCurrentQuestionAnswered() && !isLastQuestion()}
                  style={{
                    opacity:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? 0.5
                        : 1,
                    cursor:
                      !isCurrentQuestionAnswered() && !isLastQuestion()
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={
                    isLastQuestion() && allQuestionsAnswered()
                      ? submitQuiz
                      : nextQuestion
                  }
                >
                  {isLastQuestion() && allQuestionsAnswered()
                    ? "Submit"
                    : "Next"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Prompt Input Popup Modal */}
        {showPromptPopup && (
          <div className="prompt-popup-overlay">
            <div className="prompt-popup-modal">
              <div className="popup-header">
                <h3>Create New Prompt</h3>
                <button
                  className="popup-close-btn"
                  onClick={handleCancelPrompt}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="popup-content">
                <textarea
                  value={tempPromptAnswer}
                  onChange={handleTempPromptChange}
                  placeholder="Write your improved prompt here..."
                  className="popup-prompt-input"
                  rows={8}
                  autoFocus
                />
                <div className="popup-prompt-counter">
                  {tempPromptAnswer.length} characters
                </div>
              </div>
              <div className="popup-actions">
                <button
                  className="popup-cancel-btn"
                  onClick={handleCancelPrompt}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Cancel
                </button>
                <button
                  className="popup-submit-btn"
                  onClick={handleSubmitPrompt}
                  disabled={!tempPromptAnswer.trim()}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Submit Prompt
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;
